// Reusable Knowledge Base shell: sticky left nav + sharp typography right column.
// Used by /resources (public) and /dashboard/help (authenticated client).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, BookOpen, ArrowRight } from "lucide-react";
import { KB_CHAPTERS } from "../lib/knowledgeBase";

export default function KnowledgeBaseShell({ mode = "public" }) {
  const [active, setActive] = useState(KB_CHAPTERS[0].id);

  // Scroll-spy: highlight the chapter whose heading is closest to the top
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.1, 0.5] },
    );
    KB_CHAPTERS.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const eyebrow = useMemo(() =>
    mode === "dashboard" ? "Help & Documentation" : "Platform Playbook",
  [mode]);

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="kb-shell">
      {/* Header strip */}
      <header className="bg-white border-b border-[var(--rl-border)]" data-testid="kb-header">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]" data-testid="kb-eyebrow">
            <BookOpen className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-4" data-testid="kb-title">
            How Adrigo Insights AI works.
          </h1>
          <p className="mt-5 text-base lg:text-lg text-[var(--rl-text-secondary)] max-w-3xl leading-relaxed" data-testid="kb-lede">
            Everything you need to launch your first study, fund it through Stripe, route external participants via our Programmatic Panel (which also handles their payouts), and read the acoustic emotion timeline on your executive report. Five chapters, written by the team that built the pipeline.
          </p>
          {mode === "public" && (
            <div className="mt-7 flex flex-col sm:flex-row gap-3" data-testid="kb-cta-row">
              <Link to="/register/client" className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-6 py-3 text-sm font-semibold hover:bg-[var(--rl-brand)] transition-colors" data-testid="kb-cta-launch">
                Launch a study <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 bg-white border border-[var(--rl-text)] text-[var(--rl-text)] px-6 py-3 text-sm font-semibold hover:bg-[var(--rl-text)] hover:text-white transition-colors" data-testid="kb-cta-signin">
                Already a client — sign in
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Two-column body */}
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-12 gap-10">
        {/* Sticky left nav */}
        <aside className="lg:col-span-3" data-testid="kb-sidebar">
          <div className="lg:sticky lg:top-24">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-3">
              Chapters
            </div>
            <nav className="flex flex-col gap-1" data-testid="kb-nav">
              {KB_CHAPTERS.map((c, i) => {
                const Icon = c.icon;
                const isActive = active === c.id;
                return (
                  <a
                    key={c.id}
                    href={`#${c.id}`}
                    onClick={() => setActive(c.id)}
                    className={`group flex items-start gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${isActive ? "border-[var(--rl-brand)] bg-white text-[var(--rl-text)]" : "border-transparent text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] hover:bg-white/60"}`}
                    data-testid={`kb-nav-${c.id}`}
                  >
                    <span className={`font-mono text-[10px] mt-0.5 ${isActive ? "text-[var(--rl-brand)]" : "text-[var(--rl-text-secondary)]"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1">
                      <span className={`inline-flex items-center gap-2 font-semibold ${isActive ? "text-[var(--rl-text)]" : ""}`}>
                        <Icon className="w-3.5 h-3.5 shrink-0" /> {c.title}
                      </span>
                      <span className={`block text-[11px] leading-snug mt-0.5 ${isActive ? "text-[var(--rl-text-secondary)]" : "text-[var(--rl-text-secondary)]/80"}`}>
                        {c.summary}
                      </span>
                    </span>
                  </a>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content column */}
        <main className="lg:col-span-9 space-y-16" data-testid="kb-content">
          {KB_CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <section key={c.id} id={c.id} className="scroll-mt-24" data-testid={`kb-chapter-${c.id}`}>
                <div className="flex items-baseline gap-4 mb-1">
                  <span className="font-mono text-xs text-[var(--rl-text-secondary)] uppercase tracking-[0.3em]">
                    Chapter {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="h-px flex-1 bg-[var(--rl-border)]" />
                </div>
                <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-2 flex items-center gap-3" data-testid={`kb-chapter-title-${c.id}`}>
                  <Icon className="w-7 h-7 text-[var(--rl-brand)]" /> {c.title}
                </h2>
                <p className="mt-3 text-base lg:text-lg text-[var(--rl-text-secondary)] leading-relaxed max-w-3xl">
                  {c.summary}
                </p>
                <div className="mt-8 bg-white border border-[var(--rl-border)] divide-y divide-[var(--rl-border)]">
                  {c.sections.map((s, j) => (
                    <div key={`${c.id}-sec-${j}-${s.h}`} className="p-6 lg:p-8" data-testid={`kb-section-${c.id}-${j}`}>
                      <h3 className="font-display font-bold text-lg lg:text-xl tracking-tight text-[var(--rl-text)] flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-[var(--rl-brand)]" /> {s.h}
                      </h3>
                      <div className="mt-3 space-y-3 text-[15px] leading-[1.7] text-[var(--rl-text-secondary)]">
                        {s.body.map((para, k) => (
                          <p key={`${c.id}-${j}-p-${k}`}>{para}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Footer CTA */}
          <section className="bg-[var(--rl-text)] text-white p-8 lg:p-12 grid sm:grid-cols-[1fr_auto] items-center gap-6" data-testid="kb-footer-cta">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/70">
                Ready to ship a study?
              </div>
              <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight mt-2">
                From brief to boardroom-ready PPTX in 72 hours.
              </h2>
            </div>
            <Link
              to={mode === "dashboard" ? "/client/campaigns/new" : "/register/client"}
              className="inline-flex items-center gap-2 bg-[var(--rl-brand)] text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-[var(--rl-text)] transition-colors"
              data-testid="kb-footer-cta-btn"
            >
              {mode === "dashboard" ? "Build a new campaign" : "Get started"} <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </main>
      </div>
    </div>
  );
}
